import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2, Play, Pause, Pencil, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    getCommentAutoReplies,
    createCommentAutoReply,
    updateCommentAutoReply,
    deleteCommentAutoReply,
    duplicateCommentAutoReply,
    CommentAutoReply,
} from '@/services/api/comment-auto-replies';
import PageMultiSelect from './components/PageMultiSelect';

export default function CommentAutoReplies() {
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createPageIds, setCreatePageIds] = useState<string[]>([]);

    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data, isLoading, error } = useQuery({
        queryKey: ['comment-auto-replies'],
        queryFn: getCommentAutoReplies,
    });

    const rules = data ?? [];

    const createMutation = useMutation({
        mutationFn: createCommentAutoReply,
        onSuccess: (created) => {
            toast({ title: 'Regra criada!' });
            queryClient.invalidateQueries({ queryKey: ['comment-auto-replies'] });
            setIsCreateOpen(false);
            setCreateName('');
            setCreatePageIds([]);
            // Navega pro builder da regra criada
            navigate(`/meta/comment-auto-replies/${created.id}`);
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro ao criar regra', description: error.response?.data?.detail || 'Ocorreu um erro.' });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => updateCommentAutoReply(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comment-auto-replies'] });
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro', description: error.response?.data?.detail || 'Ocorreu um erro.' });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteCommentAutoReply,
        onSuccess: () => {
            toast({ title: 'Regra excluída!' });
            queryClient.invalidateQueries({ queryKey: ['comment-auto-replies'] });
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro ao excluir', description: error.response?.data?.detail || 'Ocorreu um erro.' });
        },
    });

    const duplicateMutation = useMutation({
        mutationFn: duplicateCommentAutoReply,
        onSuccess: (copy) => {
            toast({ title: 'Regra duplicada!', description: `"${copy.name}" criada desativada — revise e ative quando quiser.` });
            queryClient.invalidateQueries({ queryKey: ['comment-auto-replies'] });
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro ao duplicar', description: error.response?.data?.detail || 'Ocorreu um erro.' });
        },
    });

    const handleCreate = () => {
        if (!createName.trim() || createPageIds.length === 0) return;
        createMutation.mutate({
            name: createName,
            page_ids: createPageIds,
            reply_message: '',
            like_comment: true,
            hide_comment: false,
            auto_translate: false,
            funnel_id: null,
            trigger_node_id: null,
        });
    };

    const handleToggleActive = (rule: CommentAutoReply) => {
        updateMutation.mutate({ id: rule.id, data: { is_active: !rule.is_active } });
    };

    const handleDelete = (rule: CommentAutoReply) => {
        if (!confirm(`Excluir "${rule.name}"?`)) return;
        deleteMutation.mutate(rule.id);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Auto-Resposta de Comentários</h1>
                    <p className="text-sm text-gray-500 mt-1">Gerencie respostas automáticas aos comentários</p>
                </div>
                <Button onClick={() => setIsCreateOpen(true)} className="bg-[#0f172a] hover:bg-[#0a1120] text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Regra
                </Button>
            </div>

            {/* Loading */}
            {isLoading && (
                <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f172a]" />
                        <p className="ml-3 text-gray-500">Carregando...</p>
                    </div>
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="rounded-2xl bg-red-50 border border-red-200/50 p-6">
                    <p className="text-red-700 font-medium">Erro ao carregar regras.</p>
                </div>
            )}

            {/* Lista de regras */}
            {!isLoading && !error && rules.length > 0 && (
                <div className="space-y-3">
                    {rules.map((rule) => (
                        <div
                            key={rule.id}
                            className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all cursor-pointer group"
                            onClick={() => navigate(`/meta/comment-auto-replies/${rule.id}`)}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3">
                                        <h3 className="font-semibold text-gray-900 truncate">{rule.name}</h3>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                            rule.is_active
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-gray-100 text-gray-500'
                                        }`}>
                                            {rule.is_active ? 'Ativa' : 'Inativa'}
                                        </span>
                                        {rule.created_by_name && (
                                            <span
                                                className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200/50"
                                                title="Regra criada por um colaborador seu"
                                            >
                                                por {rule.created_by_name}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {rule.page_ids.length} página{rule.page_ids.length !== 1 ? 's' : ''}
                                    </p>
                                </div>

                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => navigate(`/meta/comment-auto-replies/${rule.id}`)}
                                        title="Editar flow"
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => duplicateMutation.mutate(rule.id)}
                                        disabled={duplicateMutation.isPending}
                                        title="Duplicar regra (a cópia nasce desativada)"
                                    >
                                        <Copy className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleToggleActive(rule)}
                                        title={rule.is_active ? 'Desativar' : 'Ativar'}
                                    >
                                        {rule.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDelete(rule)}
                                        className="hover:text-red-500"
                                        title="Excluir"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty state */}
            {!isLoading && !error && rules.length === 0 && (
                <div className="rounded-2xl bg-white p-12 shadow-sm border border-gray-100 text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma regra configurada</h3>
                    <p className="text-gray-600 mb-6">
                        Crie sua primeira regra para responder automaticamente aos comentários.
                    </p>
                    <Button onClick={() => setIsCreateOpen(true)} className="bg-[#0f172a] hover:bg-[#0a1120] text-white">
                        <Plus className="w-4 h-4 mr-2" />
                        Criar Primeira Regra
                    </Button>
                </div>
            )}

            {/* Modal de Criar */}
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Nova Regra de Auto-Resposta</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                            <Label>Nome da Regra *</Label>
                            <Input
                                value={createName}
                                onChange={e => setCreateName(e.target.value)}
                                placeholder="Ex: Promoção Black Friday"
                            />
                        </div>

                        <PageMultiSelect
                            selectedPageIds={createPageIds}
                            onChange={setCreatePageIds}
                        />

                        <div className="flex justify-end gap-2 pt-4 border-t">
                            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleCreate}
                                disabled={createMutation.isPending || !createName.trim() || createPageIds.length === 0}
                                className="bg-[#0f172a] hover:bg-[#0a1120] text-white"
                            >
                                {createMutation.isPending ? 'Criando...' : 'Criar e Configurar'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
